import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import {
  publishedConcepts,
  conceptTopics,
  formatConceptDate,
} from "@/lib/concepts";

export const Route = createFileRoute("/concepts/")({
  head: () => ({
    meta: [
      { title: "Near & Far — Notes on Optics & Making | Double Lion" },
      {
        name: "description",
        content:
          "Working notes on optics, materials, manufacturing and field use from the Double Lion studio.",
      },
      { property: "og:title", content: "Near & Far — Notes on Optics & Making" },
      {
        property: "og:description",
        content:
          "Working notes on optics, materials, manufacturing and field use from the Double Lion studio.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://doublelion.com/concepts" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://doublelion.com/concepts" }],
  }),
  component: ConceptsIndex,
});

function ConceptsIndex() {
  const [topic, setTopic] = useState<string | null>(null);
  const posts = topic
    ? publishedConcepts.filter((p) => p.topics.includes(topic))
    : publishedConcepts;

  const chipBase =
    "font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 border transition-colors";

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main className="pt-[84px] pb-24">
        {/* Compact masthead */}
        <section className="border-b border-ink">
          <div className="px-4 md:px-6 py-5 md:py-6 flex flex-col md:flex-row md:items-end md:justify-between gap-3">
            <div>
              <h1 className="font-mono text-lg md:text-2xl uppercase tracking-[0.08em] leading-none">
                Near &amp; Far
              </h1>
              <p className="mt-2 font-mono text-[11px] md:text-xs tracking-tight text-muted-foreground">
                Working notes on optics, materials and the way things are made.
              </p>
            </div>
          </div>
        </section>

        {/* Topic filter */}
        <div className="px-4 md:px-6 py-3 border-b border-border flex flex-wrap gap-2">
          <button
            onClick={() => setTopic(null)}
            className={`${chipBase} ${
              topic === null
                ? "border-ink bg-ink text-ink-foreground"
                : "border-border text-muted-foreground hover:border-ink hover:text-foreground"
            }`}
          >
            All notes
          </button>
          {conceptTopics.map((t) => (
            <button
              key={t}
              onClick={() => setTopic(t === topic ? null : t)}
              className={`${chipBase} ${
                topic === t
                  ? "border-ink bg-ink text-ink-foreground"
                  : "border-border text-muted-foreground hover:border-ink hover:text-foreground"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {/* Full-bleed card grid */}
        <section className="grid bg-background border-t border-border sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
          {posts.map((p) => (
            <Link
              key={p.slug}
              to="/concepts/$slug"
              params={{ slug: p.slug }}
              className="group bg-background p-5 flex flex-col border-b border-r border-border"
            >
              <div className="relative h-40 w-full flex items-center justify-center overflow-hidden">
                <img
                  src={p.image}
                  alt={`Illustration for ${p.title}`}
                  loading="lazy"
                  width={768}
                  height={768}
                  className={`w-auto object-contain mix-blend-multiply transition-transform duration-500 group-hover:scale-[1.06] ${
                    p.slug === "redefining-what-a-compact-binocular-is"
                      ? "h-[72%]"
                      : "h-full"
                  }`}
                />
              </div>
              <h2 className="mt-5 font-mono text-base uppercase tracking-[0.04em] leading-snug group-hover:opacity-60 transition-opacity">
                {p.title}
              </h2>
              <p className="mt-2 text-[13px] leading-relaxed text-muted-foreground flex-1">
                {p.summary}
              </p>
              <div className="mt-4 -mx-5 -mb-5 px-5 py-2.5 border-t border-border flex items-center justify-end font-mono text-[9px] uppercase tracking-[0.2em] text-muted-foreground group-hover:bg-ink group-hover:text-ink-foreground transition-colors">
                <span>
                  {formatConceptDate(p.updated)} — {p.readingMinutes} min
                </span>
              </div>

            </Link>
          ))}
        </section>
      </main>
    </div>
  );
}
